import { useFormaliteService } from '@europrocurement/l2d-domain';
import React, {
    createContext,
    PropsWithChildren,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';
import { type TypeDeDocumentJsonldTypedocumentRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { DataProviderProps, DataState } from './types';

type TypeDocumentState = TypeDeDocumentJsonldTypedocumentRead;
type TypeDocumentProviderState = DataState & {
    data: TypeDeDocumentJsonldTypedocumentRead[];
};

const INIT_TYPE_DOCS_CTX = 'init_type_documents_ctx';

type Action = { type: 'init_type_documents_ctx'; payload: TypeDocumentState[] };

const TypeDocumentsContext = createContext<TypeDocumentProviderState | null>(null);
const TypeDocumentsDispatchContext = createContext<React.Dispatch<Action> | null>(null);

function TypeDocumentsReducer(state: TypeDocumentProviderState, action: Action) {
    switch (action.type) {
        case INIT_TYPE_DOCS_CTX: {
            return {
                loaded: true,
                data: [...action.payload],
            };
        }
        default: {
            throw new Error(`Unknown action: ${action.type}`);
        }
    }
}

const TypeDocumentsProvider: React.FunctionComponent<PropsWithChildren<DataProviderProps>> =
    function ({ autoload, children }) {
        const [state, dispatch] = useReducer(TypeDocumentsReducer, { loaded: false, data: [] });
        const { typeDocApi } = useFormaliteService();
        const [isFetching, setIsFetching] = useState<boolean>(false);
        useEffect(() => {
            if (!autoload || state.loaded === true || isFetching === true) {
                return;
            }
            setIsFetching(true);
            typeDocApi
                .apiTypesDocumentsGetCollection({ itemsPerPage: 999 })
                .then((response) => {
                    dispatch({
                        type: INIT_TYPE_DOCS_CTX,
                        payload: response.data['hydra:member'],
                    });
                })
                .catch((e) => console.error(e))
                .finally(() => {
                    setIsFetching(false);
                });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [typeDocApi, autoload, state.loaded]);

        return (
            <TypeDocumentsContext.Provider value={state}>
                <TypeDocumentsDispatchContext.Provider value={dispatch}>
                    {children}
                </TypeDocumentsDispatchContext.Provider>
            </TypeDocumentsContext.Provider>
        );
    };

const useTypeDocumentsCtx = () => {
    const state = useContext(TypeDocumentsContext);

    const isDataLoaded = (): boolean => state?.loaded || false;

    const getTypeDocuments = (): TypeDocumentState[] => state?.data || [];

    const getTypeDocumentById = (id: number): TypeDocumentState | undefined =>
        (state?.data || []).find((TypeDocument) => TypeDocument.id === id);

    const getTypeDocumentsFiltered = (
        filters: Partial<Record<keyof TypeDocumentState, unknown>>,
    ): TypeDocumentState[] =>
        (state?.data || []).filter((item) =>
            Object.entries(filters).every(
                ([key, value]) => item[key as keyof TypeDocumentState] === value,
            ),
        );

    const getTypeDocumentFiltered = (
        filters: Partial<Record<keyof TypeDocumentState, unknown>>,
    ): TypeDocumentState | undefined => getTypeDocumentsFiltered(filters)[0] || undefined;

    return {
        isDataLoaded,
        getTypeDocuments,
        getTypeDocumentById,
        getTypeDocumentsFiltered,
        getTypeDocumentFiltered,
    };
};

const useTypeDocumentsCtxDispatch = () => useContext(TypeDocumentsDispatchContext);

// Méthode get
export {
    TypeDocumentsProvider,
    TypeDocumentsContext,
    TypeDocumentsDispatchContext,
    // hooks
    useTypeDocumentsCtx,
    useTypeDocumentsCtxDispatch,
    // Type Doc actions dispatch
    INIT_TYPE_DOCS_CTX,
};
